import type { CSSProperties } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { bezierCurve } from 'styles/tools/constants'
import clsx from 'clsx'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import GoogleLongLogo from 'components/Common/TrustIndicators/GoogleLongLogo'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TrustPilotLogoCombined from 'components/Account/AccountAccess/TrustPilotLogoCombined'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const SLIDE_COUNT = 3
const SLIDE_DURATION_MS = 3000

const USPs = styled.div`
  display: grid;
  overflow: visible;
  grid-template-columns: repeat(${SLIDE_COUNT}, 100%);
  width: 100%;
  transition: transform 0.5s ${bezierCurve};

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(180)} ${rem(400)};
    gap: ${rem(8)} 0;

    grid-template-areas:
      "atol atol"
      "google trustpilot";
    grid-template-columns: unset;
    gap: ${rem(8)};

    .atol-logo {
      grid-area: atol;
      display: flex;
      justify-content: center;
    }
    .trustpilot-logo {
      grid-area: trustpilot;
    }
    .google-logo {
      grid-area: google;
    }

    &.is-sign-up-modal {
      grid-template-columns: repeat(3, auto);
      grid-template-areas:
      "atol google trustpilot";
      grid-gap: ${rem(16)};
    }
  }
`

const AnimationContainer = styled.div`
  overflow: hidden;
`

export function TrustIndicatorUK() {
  const intl = useIntl()

  const isCarouselEnabled = !useScreenSizeUp('tablet')
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(SLIDE_COUNT, SLIDE_DURATION_MS, { enabled: isCarouselEnabled })

  const slideStyle: CSSProperties | undefined = isCarouselEnabled ? {
    transform: `translateX(${-activeIndex * 100}%)`,
  } : undefined

  return (
    <AnimationContainer>
      <USPs className={clsx('is-sign-up-modal')} style={slideStyle}>
        <Group className="atol-logo" direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
          <img src={getSVGImageUrl('984lj4elpskyg828z90qb')} width={20} height={20} alt="" />
          <BodyTextBlock colour="neutral-two" weight="bold" variant="medium" wrap="no-wrap">
            {intl.formatMessage({
              id: 'VxW64n9lfPWXpeyYxquy',
              defaultMessage: 'ATOL protected',
              description: 'ATOL protection label in UK trust indicator in the insurance flow',
            })}
          </BodyTextBlock>
        </Group>
        <Group className="google-logo" direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
          <GoogleLongLogo width="54" height="18" />
          <BodyTextBlock colour="neutral-two" variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'NxdXpumsvI6BCE0yXas8',
              defaultMessage: '4.7/5',
              description: 'Google rating score in trust indicator on the homepage',
            })}
          </BodyTextBlock>
        </Group>
        <Group className="trustpilot-logo" direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
          <TrustPilotLogoCombined />
          <BodyTextBlock colour="neutral-two" weight="bold" variant="medium" wrap="no-wrap">
            {intl.formatMessage({
              id: 'rgp5rPUfA8Ke2p3zj3Rm',
              defaultMessage: '4.8/5',
              description: 'Trustpilot rating score in trust indicator on the homepage',
            })}
          </BodyTextBlock>
        </Group>
      </USPs>
      <CSSBreakpoint max="mobile">
        <PaginationDots
          count={SLIDE_COUNT}
          activeIndex={activeIndex}
          slideDuration={SLIDE_DURATION_MS}
          onSelect={goToSlide}
          paused={paused}
          onHoverChange={setPaused}
        />
      </CSSBreakpoint>
    </AnimationContainer>
  )
}
